import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight, ExternalLink } from 'lucide-react';

export default function PublicationsPage() {
  const publications = [
    {
      id: 'star-compass',
      title: 'Oumuamua and the Star-Sung Compass',
      subtitle: 'A Tale of Tuwaqachi',
      description: 'Before the Games. Before the Maw. Before the blue along the fin—there was a droplet world, two moons that would not agree, and a child who asked if the Bright Whale was real.',
      image: '/publications/oumuamua/star-compass-1.jpg',
      link: '/publications/star-compass',
      tag: 'Book 1',
      isNew: true
    },
    {
      id: 'blue-flame',
      title: 'Oumuamua and the Blue Flame',
      subtitle: 'A Second Tale of Tuwaqachi',
      description: 'Five travelers ride the rifts: a scout who turns darkness into a road, a tide-reader who sings the tear before it forms, a coral-reader who listens to planets.',
      image: '/publications/oumuamua/blue-flame-2.jpg',
      link: '/publications/blue-flame',
      tag: 'Book 2',
      isNew: true
    },
    {
      id: 'truth-is-verb',
      title: 'Truth Is a Verb',
      subtitle: 'Publications & Initiatives',
      description: 'An ongoing exploration of sociological storytelling, media literacy, and the intersection of education and entertainment.',
      image: '/stills/battersea.jpg',
      link: '/truth-is-verb',
      tag: 'Series',
      isNew: false
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 px-4" style={{ background: 'linear-gradient(180deg, #0a1628 0%, #1a1a2e 100%)' }}>
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-[#C9A227] text-sm tracking-[0.3em] uppercase mb-4">Grad Fawkes</p>
          <h1 className="text-5xl md:text-7xl font-serif text-[#F3EAD3] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Publications
          </h1>
          <p className="text-xl text-[#B7C4D4] max-w-2xl mx-auto">
            Stories that bridge worlds. Tales woven from mythology, sociology, and the spaces between stars.
          </p>
        </div>
      </section>

      {/* Featured: Oumuamua Series */}
      <section className="py-16 px-4" style={{ background: 'linear-gradient(180deg, #1a1a2e 0%, #0a1628 100%)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-1 bg-[#C9A227]" />
            <h2 className="text-2xl text-[#C9A227] font-semibold">Featured Series</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {publications.filter(p => p.id.includes('compass') || p.id.includes('flame')).map((pub) => (
              <Link 
                key={pub.id}
                to={pub.link}
                className="group relative rounded-2xl overflow-hidden bg-[#0d1f3c] border border-[#C9A227]/20 hover:border-[#C9A227]/50 transition-all duration-300"
                data-testid={`pub-card-${pub.id}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img 
                    src={pub.image} 
                    alt={pub.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] via-[#0a1628]/50 to-transparent" />
                </div>
                
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-2 mb-2">
                    {pub.isNew && (
                      <span className="px-2 py-1 bg-[#C9A227] text-[#0a1628] text-xs font-bold rounded">NEW</span>
                    )}
                    <span className="text-[#C9A227] text-sm">{pub.tag}</span>
                  </div>
                  <h3 className="text-2xl font-serif text-[#F3EAD3] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {pub.title}
                  </h3>
                  <p className="text-[#C9A227] italic mb-3">{pub.subtitle}</p>
                  <p className="text-[#B7C4D4] text-sm line-clamp-2">{pub.description}</p>
                  
                  <div className="mt-4 flex items-center gap-2 text-[#C9A227] group-hover:gap-4 transition-all">
                    <span className="font-semibold">Read More</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Other Publications */}
      <section className="py-16 px-4 bg-card">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-serif text-foreground mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            More Publications
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {publications.filter(p => !p.id.includes('compass') && !p.id.includes('flame')).map((pub) => (
              <Link 
                key={pub.id}
                to={pub.link}
                className="group rounded-xl overflow-hidden bg-muted hover:bg-muted/80 transition-colors"
                data-testid={`pub-card-${pub.id}`}
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={pub.image} 
                    alt={pub.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-4">
                  <span className="text-secondary text-xs font-semibold">{pub.tag}</span>
                  <h3 className="text-lg font-semibold text-foreground mt-1">{pub.title}</h3>
                  <p className="text-muted-foreground text-sm mt-2 line-clamp-2">{pub.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Social Links */}
      <section className="py-12 px-4 bg-[#0a1628]">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl text-[#F3EAD3] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Follow the Journey
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://www.facebook.com/profile.php?id=61583179743030" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1877F2] text-white font-semibold rounded-lg hover:bg-[#1877F2]/80 transition-colors"
            >
              Facebook
              <ExternalLink className="w-4 h-4" />
            </a>
            <a 
              href="https://www.instagram.com/cocoalife.min/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
            >
              Instagram
              <ExternalLink className="w-4 h-4" />
            </a>
            <a 
              href="https://www.tiktok.com/@taleoftuwaqachi" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-900 transition-colors border border-white/20"
            >
              TikTok
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
